///
/// EarthBrowser
///
/// Created by Matt Giger on 9/6/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { DrapedPolygonGeometry } from "@/earthbrowser/entities/geometries/DrapedPolygonGeometry"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { SimpleNode } from "@/earthbrowser/entities/SimpleNode/SimpleNode"
import { cSurfaceOverlayDepthBias } from "@/earthbrowser/entities/surfaceDepthBias"
import { geographic } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type color } from "@stovokor/math/color"
import { trig } from "@stovokor/math/math"
import { vec2 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import type { WindRadii, WindRadiiQuadrants } from "./HurricanesTable"
import { destinationLatLon } from "./stormTrack"

const cNmToKm = 1.852
const cEarthRadiusKm = 6371
const cRingSamples = 96
const cOutlineWidth = 1.5
// The outline floats slightly above the fill so it wins the depth test
const cOutlineLiftKm = 0.15

interface RingStyle {
	threshold: "34" | "50" | "64"
	fill: color
	outline: color
}

// Drawn largest-first so the translucent fills stack toward the center
const cRingStyles: RingStyle[] = [
	{ threshold: "34", fill: DesignColors.STORM_WIND_34_FILL, outline: DesignColors.STORM_WIND_34_OUTLINE },
	{ threshold: "50", fill: DesignColors.STORM_WIND_50_FILL, outline: DesignColors.STORM_WIND_50_OUTLINE },
	{ threshold: "64", fill: DesignColors.STORM_WIND_64_FILL, outline: DesignColors.STORM_WIND_64_OUTLINE },
]

/**
 * Current wind extent for one storm: draped translucent rings for the 34, 50
 * and 64 kt radii from the latest advisory, built from the per-quadrant
 * radii in the NHC forecast advisory. Quadrant values are blended with a
 * cosine ease between quadrant centers so the ring reads as one asymmetric
 * wind field instead of four abutting arcs. The rings are anchored at the
 * advisory-time position — they describe the storm now, not while scrubbing.
 */
export class StormWindFieldNode extends NodeBase {
	constructor(
		context: StovokorContext,
		radii: WindRadii,
		centerLat: number,
		centerLon: number,
		altitudeKm: number
	) {
		super()

		for (const style of cRingStyles) {
			const quadrants = radii[style.threshold]
			if (!quadrants) continue

			const ring = buildRingLatLon(quadrants, centerLat, centerLon)
			if (ring.length < 3) continue

			try {
				const fillGeometry = new DrapedPolygonGeometry(
					context,
					ring.map(p => new vec2(p.lon, p.lat)),
					altitudeKm
				)
				this.addNode(new SimpleNode(context, fillGeometry, style.fill, { polygonOffset: cSurfaceOverlayDepthBias }))
			} catch {
				// Degenerate ring: keep the outline
			}

			const outlinePositions = ring.map(p =>
				new geographic(p.lon * trig.rad, p.lat * trig.rad, altitudeKm + cOutlineLiftKm).cartesian
			)
			this.addNode(new PolylineNode(context, {
				positions: outlinePositions,
				width: cOutlineWidth,
				color: style.outline,
				loop: true,
				depthWrite: false,
				polygonOffset: cSurfaceOverlayDepthBias,
			}))
		}
	}
}

/**
 * Sample the asymmetric wind ring: radius at each azimuth blends the two
 * nearest quadrant radii (quadrant centers at 45/135/225/315 degrees)
 */
function buildRingLatLon(
	quadrants: WindRadiiQuadrants,
	centerLat: number,
	centerLon: number
): { lat: number, lon: number }[] {
	const values = [quadrants.ne, quadrants.se, quadrants.sw, quadrants.nw]
	if (!values.some(v => v > 0)) return []

	const ring: { lat: number, lon: number }[] = []
	for (let i = 0; i < cRingSamples; i++) {
		const azimuthDeg = (i / cRingSamples) * 360
		// Which pair of quadrant centers brackets this azimuth
		const sector = Math.floor(((azimuthDeg + 360 - 45) % 360) / 90)
		const fraction = (((azimuthDeg + 360 - 45) % 360) % 90) / 90
		const eased = (1 - Math.cos(fraction * Math.PI)) / 2
		const radiusNm = values[sector % 4] * (1 - eased) + values[(sector + 1) % 4] * eased
		// Zero-radius quadrants pull the ring into the center; keep a small
		// floor so the polygon stays simple for triangulation
		const radiusKm = Math.max(radiusNm * cNmToKm, 2)
		ring.push(destinationLatLon(centerLat, centerLon, azimuthDeg * trig.rad, radiusKm / cEarthRadiusKm))
	}
	return ring
}
